// Keep an ACCESS-ONLY session alive on an SDK that has no session reacquirer.
//
// The bridge hands a peer origin the access token only (sessionBridgeTokens.js).
// The SDK hook that makes the peer re-ask on expiry —
// TokenManager.setSessionReacquirer, SDK branch
// fix/token-manager-session-reacquirer — ships with the next SDK release. An
// SDK without it CLEARS an expired session that has no refresh token
// (TokenManager.ensureValidToken), which would sign the peer out once per
// access-token lifetime: the very symptom this fix removes.
//
// So, on such an SDK only, this re-asks the bridge `leadMs` before the access
// token expires (the TokenManager treats a token as expired 60 s early, so the
// lead is larger than that; the owner's bridge rotates its own token when less
// than 5 min remain, so the answer is a fresh token) and hands the new token to
// the TokenManager through
// its public `setTokens` — the call magic-callback.js already makes. It
// re-schedules after every renewal, retries every `retryMs` while the old token
// still lives, and re-checks when the tab becomes visible again (timers in a
// hidden tab are throttled).
//
// It does nothing when:
//   - the SDK has the reacquirer hook (the SDK owns the re-ask then);
//   - the session owns a refresh token (the owner refreshes itself);
//   - there is no session at all.
//
// Returns `{ stop, renewNow }`, or null when nothing was installed.
//
// FA204 / frank trap — helpers stay inline in the export.

import { reacquireBridgeSession } from './sessionBridgeReacquire.js'

export const installBridgeSessionRenewal = (sdk, opts = {}) => {
  const tm = sdk?._tokenManager
  if (!tm || typeof tm.setTokens !== 'function' || typeof tm.getTokenStatus !== 'function') {
    return null
  }
  if (typeof tm.setSessionReacquirer === 'function') return null

  const leadMs = opts.leadMs ?? 2 * 60 * 1000
  const retryMs = opts.retryMs ?? 30 * 1000
  const reacquire = opts.reacquire || (() => reacquireBridgeSession())
  let timer = null
  let stopped = false
  let inFlight = null

  const isPeerSession = () => tm.hasTokens() && !tm.hasRefreshToken()
  const expiresAt = () => tm.getTokenStatus()?.expiresAt || null

  const clear = () => {
    if (timer) clearTimeout(timer)
    timer = null
  }
  const schedule = (delay) => {
    clear()
    if (stopped || !isPeerSession()) return
    const exp = expiresAt()
    if (!exp) return
    const wait = delay ?? Math.max(0, exp - leadMs - Date.now())
    timer = setTimeout(() => {
      timer = null
      renewNow()
    }, wait)
  }
  const renewNow = () => {
    if (inFlight) return inFlight
    inFlight = (async () => {
      if (stopped || !isPeerSession()) return false
      let got = null
      try {
        got = await reacquire()
      } catch {
        got = null
      }
      if (stopped) return false
      if (got?.access_token && isPeerSession()) {
        const expiresIn = got.expires_at
          ? Math.max(1, Math.floor((got.expires_at - Date.now()) / 1000))
          : undefined
        tm.setTokens({
          access_token: got.access_token,
          expires_in: expiresIn,
          token_type: 'Bearer'
        })
        // The owner may hand back a token that is no newer (it had not
        // rotated yet): retry later instead of re-asking at delay 0.
        const exp = expiresAt()
        if (exp && exp - Date.now() <= leadMs) {
          if (exp - Date.now() > retryMs) schedule(retryMs)
        } else schedule()
        return true
      }
      const exp = expiresAt()
      if (exp && exp - Date.now() > retryMs) schedule(retryMs)
      return false
    })().finally(() => {
      inFlight = null
    })
    return inFlight
  }

  const onVisible = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'visible') schedule()
  }
  try {
    document.addEventListener('visibilitychange', onVisible)
  } catch {
    /* no document (SSR) */
  }
  schedule()

  return {
    stop: () => {
      stopped = true
      clear()
      try {
        document.removeEventListener('visibilitychange', onVisible)
      } catch {
        /* no document */
      }
    },
    renewNow
  }
}
